/* eslint-disable no-alert */
import { useAuth } from '@contexts/Auth'
import { useModal } from '@contexts/Modal'
import { Logout } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { isMobile } from 'react-device-detect'
import { SVGProps, useState } from 'react'
import { ShareCheckoutModal } from '@containers/Modals'
import { AppBannerUnpaid } from '@containers/AppBannerUnpaid'
import { AppBannerPastdue } from '@containers/AppBannerPastdue'
import { StripePortalLink } from '@containers/StripePortalLink'
import { NotificationBadge } from '@containers/NotificationBadge'
import { SizedComponent } from '@components/atoms'
import { Toolbar, Typography, Menu, Stack } from '@mui/material'
import AppBannerFreeTrialExpiration from '@containers/AppBannerFreeTrialExpiration'

import { PlusUserIcon, UserIcon } from '@components/icons'

import {
  MenuButton,
  LogoContainer,
  MenuContainer,
  ApplicationBar,
  MenuItemWrapper,
} from './AppBar.styles'

import { DesktopMenu } from './components/DesktopMenu/DesktopMenu'
import { MobileMenu } from './components/MobileMenu/MobileMenu'
import { WhatsAppPaymentLink } from '@containers/WhatsAppPaymentLink'
import { useContextSelector } from 'use-context-selector'
import InputSearch from './components/SearchBar/InputSearch'
import { generateInitials } from '@utils/functions/string/string'
import { HomeContext } from '@pages/private/Home/context'
import { SentinelBadge } from '@containers/SentinelBadge'

export type NomosModuleProps = {
  id: string
  icon: (props: SVGProps<SVGSVGElement>) => JSX.Element
  name: string
  path: string
  options?: [] | undefined
}

export type Page = {
  name: string
  path: string
  options: string[] | undefined
}

export type AppBarComponentProps = {
  logo: React.ReactElement
  pages?: Page[]
  onChangeHeight?: (height: number) => void
}

function AppBarComponent({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  logo,
  pages,
  onChangeHeight,
}: AppBarComponentProps) {
  const { openModal } = useModal()
  const navigate = useNavigate()
  const { user, isLogout } = useAuth()
  const search = useContextSelector(HomeContext, (s) => s.search)
  const keywords = useContextSelector(HomeContext, (s) => s.search.keywords)

  const [anchorElUser, setAnchorElUser] = useState<Element | null | undefined>(
    null
  )

  const settings = [
    {
      id: 'my-account',
      title: 'Minha conta',
      icon: <UserIcon />,
      onClick: () => navigate('/my-account'),
    },
    {
      id: 'users',
      title: 'Usuários',
      icon: <PlusUserIcon />,
      onClick: () => navigate('/users'),
    },
    {
      id: 'logout',
      title: 'Sair',
      icon: <Logout fontSize="small" />,
      onClick: isLogout,
    },
  ]

  const handleOpenUserMenu = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setAnchorElUser(event.currentTarget)
  }

  const handleCloseUserMenu = () => {
    setAnchorElUser(null)
  }

  const handleUpdateSearch = useContextSelector(HomeContext, (s) => s.setSearch)

  const handleExecuteSearch = useContextSelector(
    HomeContext,
    (s) => s.handleExecuteSearch
  )

  return (
    <ApplicationBar
      data-testid="appbar-component"
      position="static"
      className="notPrintable"
    >
      <SizedComponent onChange={({ height }) => onChangeHeight?.(height)}>
        <>
          <AppBannerPastdue />
          <AppBannerUnpaid />
          <AppBannerFreeTrialExpiration />
          <Stack
            width="98%"
            margin="0 auto"
            justifyContent="center"
            sx={{ height: 70 }}
          >
            <Toolbar disableGutters>
              {/* For mobile */}
              <MobileMenu pages={pages || []} />

              {/* For desktop */}
              <MenuButton
                size="small"
                target="_blank"
                color="secondary"
                disableRipple={false}
                onClick={() => navigate('/monitors')}
              >
                <LogoContainer>{logo}</LogoContainer>
              </MenuButton>

              {/* For desktop  */}
              <DesktopMenu pages={pages || []} />

              {/* Desktop only end  */}
              <MenuContainer>
                {!isMobile && window.location.pathname !== '/search-results' && (
                  <InputSearch
                    value={keywords}
                    size="small"
                    placeholder="Digite ENTER para inserir mais de uma palavra chave"
                    onChangeSearch={(k) =>
                      handleUpdateSearch({ keywords: k.keywords })
                    }
                    onExecuteSearch={() => {
                      handleExecuteSearch(search)
                      handleUpdateSearch({
                        keywords: { or: [], and: [], not: [] },
                      })
                    }}
                    sx={{
                      width: '100%',
                    }}
                  />
                )}

                <NotificationBadge />

                {!isMobile && (
                  <>
                    <Stack
                      sx={{
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        padding: '8px',
                        borderRadius: '22px',
                        backgroundColor: '#2970FF',
                        '& :hover': {
                          backgroundColor: '#2970FF',
                        },
                      }}
                    >
                      <Typography
                        data-cy="menu-appbar"
                        onClick={handleOpenUserMenu}
                        sx={{
                          cursor: 'pointer',
                        }}
                        variant="$font-body-4"
                        color="$color-text-light"
                        align="center"
                      >
                        {generateInitials(user?.name || '')}
                      </Typography>
                    </Stack>

                    <Menu
                      id="menu-appbar"
                      sx={{ mt: '35px' }}
                      anchorEl={anchorElUser}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      keepMounted
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      open={Boolean(anchorElUser)}
                      onClose={handleCloseUserMenu}
                    >
                      {settings.map((setting) => (
                        <MenuItemWrapper
                          key={setting.id}
                          data-cy={setting.id}
                          onClick={() => {
                            setting.onClick()
                            handleCloseUserMenu()
                          }}
                        >
                          {setting.icon}
                          <Typography
                            variant="$font-body-base"
                            color="$color-text-secondary"
                          >
                            {setting.title}
                          </Typography>
                        </MenuItemWrapper>
                      ))}

                      {!!user?.admin && !user?.company?.subscription && (
                        <MenuItemWrapper>
                          <WhatsAppPaymentLink
                            fullWidth
                            size="small"
                            text="Assinar Plano Pro"
                            title="Assinar Plano Pro"
                          />
                        </MenuItemWrapper>
                      )}

                      {!!user?.admin && !user?.company?.subscription && (
                        <MenuItemWrapper
                          onClick={() => {
                            openModal(<ShareCheckoutModal />)
                            handleCloseUserMenu()
                          }}
                        >
                          <Typography
                            variant="$font-body-3"
                            color="$color-action-link"
                          >
                            Compartilhar link de assinatura
                          </Typography>
                        </MenuItemWrapper>
                      )}

                      {!!user?.admin && user?.company?.subscription && (
                        <MenuItemWrapper>
                          <StripePortalLink
                            size="small"
                            text="Visualizar Assinatura"
                            title="Visualizar Assinatura"
                          />
                        </MenuItemWrapper>
                      )}
                    </Menu>
                  </>
                )}

                {!isMobile && <SentinelBadge />}
              </MenuContainer>
            </Toolbar>
          </Stack>
        </>
      </SizedComponent>
    </ApplicationBar>
  )
}

AppBarComponent.defaultProps = {
  pages: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  onChangeHeight: (height: number) => {},
}

export default AppBarComponent
